import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
    Layers,
    Folder,
    Hash,
    ChevronRight,
    Sparkles,
    Zap,
    ShieldAlert,
    Flame,
    Search,
    LayoutGrid,
    LayoutList,
    GanttChartSquare,
} from 'lucide-react';
import type { Task } from '@/types';
import { ViewToolbar, ToolbarSelect, FilterPill, StatCard, ViewToggleGroup, SearchInput } from '@/components/shared/view-toolbar';
import clsx from 'clsx';

import { useViewData } from '@/hooks/useViewData';
import { useSpaces } from '@/hooks/useSpaces';
import { useAppStore } from '@/store/appStore';
import { normalizeStatus, isCompletedStatus, TASK_STATUS } from '@/constants/taskStatuses';
import { ProjectsGantt } from './ProjectsGantt';

type SortOption = 'name' | 'tasks' | 'recent';
type LayoutOption = 'grid' | 'list' | 'gantt';
type FilterOption = 'all' | 'spaces' | 'folders' | 'lists';

export function ProjectsView() {
    const { spaces, categories } = useSpaces();
    const {
        enrichedTasks: tasks,
        onNavigate,
        onViewModeChange,
    } = useViewData();
    const searchFocusTrigger = useAppStore((state) => state.searchFocusTrigger);
    const [sortBy, setSortBy] = useState<SortOption>('tasks');
    const [layout, setLayout] = useState<LayoutOption>(() => {
        const saved = localStorage.getItem('zenda-projects-layout');
        return (saved === 'grid' || saved === 'list' || saved === 'gantt') ? saved : 'grid';
    });
    const handleLayoutChange = useCallback((v: string) => {
        const val = v as LayoutOption;
        setLayout(val);
        localStorage.setItem('zenda-projects-layout', val);
    }, []);
    const [filterType, setFilterType] = useState<FilterOption>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSpace, setSelectedSpace] = useState<string | null>(null);
    const [expandedListSpaces, setExpandedListSpaces] = useState<Set<string>>(new Set());
    const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Focus search input when triggered from keyboard shortcut
    useEffect(() => {
        if (searchFocusTrigger > 0) {
            searchInputRef.current?.focus();
        }
    }, [searchFocusTrigger]);

    // Calculate stats for each space
    const spacesWithStats = useMemo(() => {
        return spaces.map(space => {
            // Filter tasks by spaceId instead of spaceName
            const spaceTasks = tasks.filter(t => {
                const taskSpaceId = t.spaceId;
                return taskSpaceId === space.id;
            });
            const completedTasks = spaceTasks.filter(t => isCompletedStatus(t.status)).length;
            const inProgressTasks = spaceTasks.filter(t => normalizeStatus(t.status) === TASK_STATUS.IN_PROGRESS).length;
            const blockedTasks = spaceTasks.filter(t => normalizeStatus(t.status) === TASK_STATUS.BLOCKED).length;
            const overdueTasks = spaceTasks.filter(t => {
                if (!t.dueDate) return false;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const todayTs = today.getTime();
                return t.dueDate < todayTs && !isCompletedStatus(t.status);
            }).length;
            return {
                ...space,
                completedTasks,
                inProgressTasks,
                blockedTasks,
                overdueTasks,
                completionRate: space.taskCount > 0
                    ? Math.round((completedTasks / space.taskCount) * 100)
                    : 0
            };
        });
    }, [spaces, tasks]);

    // Global stats
    const stats = useMemo(() => {
        const totalSpaces = spaces.length;
        const totalFolders = spaces.reduce((sum, s) => sum + s.folders.length, 0);
        const totalLists = spaces.reduce((sum, s) =>
            sum + s.folders.reduce((fSum, f) => fSum + f.lists.length, 0), 0
        );
        const totalTasks = tasks.length;
        const totalOverdue = spacesWithStats.reduce((sum, s) => sum + s.overdueTasks, 0);

        return { totalSpaces, totalFolders, totalLists, totalTasks, totalOverdue };
    }, [spaces, tasks, spacesWithStats]);

    // Filter and sort spaces
    const filteredSpaces = useMemo(() => {
        let result = [...spacesWithStats];

        // Filter by search
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(s =>
                s.name.toLowerCase().includes(query) ||
                s.folders.some(f =>
                    f.name.toLowerCase().includes(query) ||
                    f.lists.some(l => l.name.toLowerCase().includes(query))
                )
            );
        }

        // Sort
        switch (sortBy) {
            case 'name':
                result.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case 'tasks':
                result.sort((a, b) => b.taskCount - a.taskCount);
                break;
            case 'recent':
                // Keep original order (assumed to be by recent activity)
                break;
        }

        return result;
    }, [spacesWithStats, searchQuery, sortBy]);

    // Group filtered spaces by category
    const hasCategories = categories.length > 0;
    const spacesByCategory = useMemo(() => {
        if (!hasCategories) return null;
        const filteredIds = new Set(filteredSpaces.map(s => s.id));
        return categories
            .map(cat => ({
                ...cat,
                projects: cat.projects.filter(p => filteredIds.has(p.id)),
                // Get the full stats version of each space
                spacesWithStats: cat.projects
                    .filter(p => filteredIds.has(p.id))
                    .map(p => filteredSpaces.find(s => s.id === p.id)!)
                    .filter(Boolean),
            }))
            .filter(cat => cat.spacesWithStats.length > 0);
    }, [categories, filteredSpaces, hasCategories]);

    // Helper to calculate stats for a set of tasks
    const calculateStats = useCallback((taskList: Task[]) => {
        const completedTasks = taskList.filter(t => isCompletedStatus(t.status)).length;
        const inProgressTasks = taskList.filter(t => normalizeStatus(t.status) === TASK_STATUS.IN_PROGRESS).length;
        const blockedTasks = taskList.filter(t => normalizeStatus(t.status) === TASK_STATUS.BLOCKED).length;
        const overdueTasks = taskList.filter(t => {
            if (!t.dueDate) return false;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const todayTs = today.getTime();
            return t.dueDate < todayTs && !isCompletedStatus(t.status);
        }).length;
        const completionRate = taskList.length > 0
            ? Math.round((completedTasks / taskList.length) * 100)
            : 0;

        return { completedTasks, inProgressTasks, blockedTasks, overdueTasks, completionRate };
    }, []);

    const handleSpaceClick = useCallback((spaceName: string) => {
        if (selectedSpace === spaceName) {
            setSelectedSpace(null);
        } else {
            setSelectedSpace(spaceName);
        }
    }, [selectedSpace]);

    const toggleListSpaceExpand = useCallback((spaceName: string) => {
        setExpandedListSpaces(prev => {
            const next = new Set(prev);
            if (next.has(spaceName)) {
                next.delete(spaceName);
            } else {
                next.add(spaceName);
            }
            return next;
        });
    }, []);

    const toggleCategory = useCallback((catId: string) => {
        setCollapsedCategories(prev => {
            const next = new Set(prev);
            if (next.has(catId)) {
                next.delete(catId);
            } else {
                next.add(catId);
            }
            return next;
        });
    }, []);

    const handleSpaceNavigate = useCallback((spaceId: string) => {
        onNavigate(spaceId);
        onViewModeChange('list');
    }, [onNavigate, onViewModeChange]);

    const handleFolderNavigate = useCallback((spaceId: string, folderId: string) => {
        onNavigate(`${spaceId}/${folderId}`);
        onViewModeChange('list');
    }, [onNavigate, onViewModeChange]);

    const handleListNavigate = useCallback((spaceId: string, folderId: string | null, listId: string) => {
        const path = folderId ? `${spaceId}/${folderId}/${listId}` : `${spaceId}/${listId}`;
        onNavigate(path);
        onViewModeChange('list');
    }, [onNavigate, onViewModeChange]);

    return (
        <div className="flex-1 flex flex-col overflow-hidden animate-fade-in">
            {/* Header */}
            <ViewToolbar
                onOpenSidebar={() => useAppStore.getState().setMobileSidebarOpen(true)}
                icon={<Layers className="w-4 h-4 text-accent-violet" />}
                title="Progetti"
                subtitle={`${stats.totalSpaces} progetti`}
                leftContent={
                    <ViewToggleGroup
                        value={layout}
                        onChange={handleLayoutChange}
                        options={[
                            { value: 'grid', icon: <LayoutGrid className="w-3.5 h-3.5" />, label: 'Griglia' },
                            { value: 'list', icon: <LayoutList className="w-3.5 h-3.5" />, label: 'Lista' },
                            { value: 'gantt', icon: <GanttChartSquare className="w-3.5 h-3.5" />, label: 'Gantt' },
                        ]}
                    />
                }
                rightContent={layout !== 'gantt' ?
                    <ToolbarSelect
                        value={sortBy}
                        onChange={(v) => setSortBy(v as SortOption)}
                        options={[
                            { value: 'tasks', label: 'Ordina per task' },
                            { value: 'name', label: 'Ordina per nome' },
                            { value: 'recent', label: 'Più recenti' },
                        ]}
                    /> : undefined
                }
                filterContent={layout !== 'gantt' ?
                    <div className="space-y-4">
                        {/* Search */}
                        <SearchInput
                            ref={searchInputRef}
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder="Cerca progetti, cartelle, liste..."
                        />

                        {/* Stats and Filter Pills */}
                        <div className="flex flex-wrap gap-2">
                            <FilterPill
                                icon={<Layers className="w-3.5 h-3.5" />}
                                label="Tutti"
                                count={stats.totalSpaces + stats.totalFolders + stats.totalLists}
                                isActive={filterType === 'all'}
                                onClick={() => setFilterType('all')}
                            />
                            <FilterPill
                                icon={<Zap className="w-3.5 h-3.5" />}
                                label="Space"
                                count={stats.totalSpaces}
                                isActive={filterType === 'spaces'}
                                onClick={() => setFilterType('spaces')}
                                color="amber"
                            />
                            <FilterPill
                                icon={<Folder className="w-3.5 h-3.5" />}
                                label="Cartelle"
                                count={stats.totalFolders}
                                isActive={filterType === 'folders'}
                                onClick={() => setFilterType('folders')}
                                color="blue"
                            />
                            <FilterPill
                                icon={<Hash className="w-3.5 h-3.5" />}
                                label="Liste"
                                count={stats.totalLists}
                                isActive={filterType === 'lists'}
                                onClick={() => setFilterType('lists')}
                                color="emerald"
                            />
                        </div>

                        {/* Overview Stats */}
                        <div className="grid grid-cols-4 gap-3">
                            <StatCard
                                icon={<Sparkles className="w-4 h-4" />}
                                label="Task totali"
                                value={stats.totalTasks.toLocaleString()}
                                color="emerald"
                            />
                            <StatCard
                                icon={<Flame className="w-4 h-4" />}
                                label="In ritardo"
                                value={stats.totalOverdue}
                                color="rose"
                            />
                            <StatCard
                                icon={<Layers className="w-4 h-4" />}
                                label="Progetti"
                                value={stats.totalSpaces}
                                color="violet"
                            />
                        </div>
                    </div> : undefined
                }
            />

            {/* Content */}
            {layout === 'gantt' ? (
                <ProjectsGantt
                    spaces={spacesWithStats}
                    tasks={tasks}
                    onSpaceNavigate={handleSpaceNavigate}
                />
            ) : (
            <div className="flex-1 overflow-y-auto p-4">
                {layout === 'grid' ? (
                    // Grid Layout - Space Cards, grouped by category if available
                    hasCategories && spacesByCategory ? (
                        <div className="space-y-4">
                            {spacesByCategory.map((cat) => {
                                const isCollapsed = collapsedCategories.has(cat.id);
                                return (
                                    <div key={cat.id}>
                                        <button
                                            onClick={() => toggleCategory(cat.id)}
                                            className="flex items-center gap-1.5 mb-3 px-1 group cursor-pointer"
                                        >
                                            <ChevronRight className={clsx(
                                                'w-3.5 h-3.5 text-text-quaternary transition-transform',
                                                !isCollapsed && 'rotate-90'
                                            )} />
                                            <span className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary group-hover:text-text-secondary transition-colors">
                                                {cat.name}
                                            </span>
                                            <span className="text-[11px] text-text-quaternary font-normal">{cat.spacesWithStats.length}</span>
                                        </button>
                                        {!isCollapsed && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                                {cat.spacesWithStats.map((space) => (
                                                    <SpaceCard
                                                        key={space.id}
                                                        space={space}
                                                        isExpanded={selectedSpace === space.name}
                                                        onClick={() => handleSpaceClick(space.name)}
                                                        onNavigate={handleSpaceNavigate}
                                                        onFolderNavigate={handleFolderNavigate}
                                                        onListNavigate={handleListNavigate}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {filteredSpaces.map((space) => (
                                <SpaceCard
                                    key={space.id}
                                    space={space}
                                    isExpanded={selectedSpace === space.name}
                                    onClick={() => handleSpaceClick(space.name)}
                                    onNavigate={handleSpaceNavigate}
                                    onFolderNavigate={handleFolderNavigate}
                                    onListNavigate={handleListNavigate}
                                />
                            ))}
                        </div>
                    )
                ) : (
                    // List Layout - Hierarchical with expandable spaces, grouped by category
                    hasCategories && spacesByCategory ? (
                        <div className="space-y-4">
                            {spacesByCategory.map((cat) => {
                                const isCollapsed = collapsedCategories.has(cat.id);
                                return (
                                    <div key={cat.id}>
                                        <button
                                            onClick={() => toggleCategory(cat.id)}
                                            className="flex items-center gap-1.5 mb-3 px-1 group cursor-pointer"
                                        >
                                            <ChevronRight className={clsx(
                                                'w-3.5 h-3.5 text-text-quaternary transition-transform',
                                                !isCollapsed && 'rotate-90'
                                            )} />
                                            <span className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary group-hover:text-text-secondary transition-colors">
                                                {cat.name}
                                            </span>
                                            <span className="text-[11px] text-text-quaternary font-normal">{cat.spacesWithStats.length}</span>
                                        </button>
                                        {!isCollapsed && (
                                            <div className="space-y-3">
                                                {cat.spacesWithStats.map((space) => {
                                                    const listsCount = space.folders.reduce((sum, f) => sum + f.lists.length, 0);
                                                    const isExpanded = expandedListSpaces.has(space.name);
                                                    return (
                                                        <SpaceListRowWithChildren
                                                            key={space.id}
                                                            space={space}
                                                            listsCount={listsCount}
                                                            isExpanded={isExpanded}
                                                            tasks={tasks}
                                                            calculateStats={calculateStats}
                                                            onToggle={() => toggleListSpaceExpand(space.name)}
                                                            onNavigate={() => handleSpaceNavigate(space.id)}
                                                            onFolderNavigate={handleFolderNavigate}
                                                            onListNavigate={handleListNavigate}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredSpaces.map((space) => {
                                const listsCount = space.folders.reduce((sum, f) => sum + f.lists.length, 0);
                                const isExpanded = expandedListSpaces.has(space.name);
                                return (
                                    <SpaceListRowWithChildren
                                        key={space.id}
                                        space={space}
                                        listsCount={listsCount}
                                        isExpanded={isExpanded}
                                        tasks={tasks}
                                        calculateStats={calculateStats}
                                        onToggle={() => toggleListSpaceExpand(space.name)}
                                        onNavigate={() => handleSpaceNavigate(space.id)}
                                        onFolderNavigate={handleFolderNavigate}
                                        onListNavigate={handleListNavigate}
                                    />
                                );
                            })}
                        </div>
                    )
                )}

                {filteredSpaces.length === 0 && (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-surface-tertiary flex items-center justify-center">
                                <Search className="w-8 h-8 text-text-quaternary" />
                            </div>
                            <p className="text-[13px] font-medium text-text-secondary">Nessun risultato</p>
                            <p className="text-[11px] text-text-quaternary mt-1">Prova con una ricerca diversa</p>
                        </div>
                    </div>
                )}
            </div>
            )}
        </div>
    );
}


// Space Card Component
interface SpaceCardProps {
    space: {
        id: string;
        name: string;
        folders: Array<{
            id?: string;
            name: string;
            lists: Array<{ id: string; name: string; taskCount: number }>;
            taskCount: number;
        }>;
        taskCount: number;
        completedTasks: number;
        inProgressTasks: number;
        blockedTasks: number;
        overdueTasks: number;
        completionRate: number;
    };
    isExpanded: boolean;
    onClick: () => void;
    onNavigate: (spaceId: string) => void;
    onFolderNavigate: (spaceId: string, folderId: string) => void;
    onListNavigate: (spaceId: string, folderId: string | null, listId: string) => void;
}

function SpaceCard({
    space,
    isExpanded,
    onClick,
    onNavigate,
    onFolderNavigate,
    onListNavigate
}: SpaceCardProps) {
    return (
        <div className={clsx(
            'rounded-xl border transition-all',
            isExpanded
                ? 'bg-surface-secondary border-accent-violet/30 shadow-lg'
                : 'bg-surface-primary border-border-primary hover:border-border-secondary hover:shadow-md'
        )}>
            {/* Space Header */}
            <div
                onClick={onClick}
                className="p-4 cursor-pointer"
            >
                <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h3
                                onClick={(e) => { e.stopPropagation(); onNavigate(space.id); }}
                                className="text-[14px] font-semibold text-text-primary truncate hover:text-accent-violet cursor-pointer transition-colors"
                            >
                                {space.name}
                            </h3>
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-surface-tertiary text-text-tertiary tabular-nums">
                                {space.taskCount.toLocaleString()} task
                            </span>
                        </div>
                        <p className="text-[11px] text-text-tertiary mt-0.5">
                            {space.folders.length} cartelle · {space.folders.reduce((sum, f) => sum + f.lists.length, 0)} liste
                        </p>
                    </div>
                    <ChevronRight className={clsx(
                        'w-5 h-5 text-text-quaternary transition-transform',
                        isExpanded && 'rotate-90'
                    )} />
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                    <div className="flex items-center justify-between text-[10px] mb-1.5">
                        <span className="text-text-quaternary uppercase tracking-wider font-medium">Completamento</span>
                        <span className="text-text-secondary font-semibold tabular-nums">{space.completionRate}%</span>
                    </div>
                    <div className="h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
                        <div
                            className="h-full bg-accent-emerald rounded-full transition-all"
                            style={{ width: `${space.completionRate}%` }}
                        />
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-2 mt-4">
                    <MiniStat icon={<Sparkles className="w-3 h-3" />} value={space.completedTasks} color="emerald" label="Completati" />
                    <MiniStat icon={<Zap className="w-3 h-3" />} value={space.inProgressTasks} color="blue" label="In corso" />
                    <MiniStat icon={<ShieldAlert className="w-3 h-3" />} value={space.blockedTasks} color="rose" label="Bloccati" />
                </div>

                {space.overdueTasks > 0 && (
                    <div className="mt-3 flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-accent-rose/10">
                        <Flame className="w-3.5 h-3.5 text-accent-rose" />
                        <span className="text-[10px] text-accent-rose font-medium">{space.overdueTasks} task in ritardo</span>
                    </div>
                )}
            </div>

            {/* Expanded Content - Folders and Lists */}
            {isExpanded && (
                <div className="px-4 pb-4 border-t border-border-primary/50 pt-4">
                    <div className="space-y-2">
                        {space.folders.map((folder) => (
                            <div key={`${space.id}/${folder.id || folder.name}`} className="rounded-lg bg-surface-tertiary/30 border border-border-primary/50 overflow-hidden">
                                <button
                                    onClick={() => folder.id ? onFolderNavigate(space.id, folder.id) : undefined}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-hover transition-colors"
                                >
                                    <Folder className="w-4 h-4 text-accent-blue" />
                                    <span className="flex-1 text-[12px] font-medium text-text-primary truncate">{folder.name}</span>
                                    <span className="text-[10px] text-text-quaternary tabular-nums">{folder.taskCount}</span>
                                    <ChevronRight className="w-3.5 h-3.5 text-text-quaternary" />
                                </button>

                                {folder.lists.length > 0 && (
                                    <div className="border-t border-border-primary/30">
                                        {folder.lists.map((list) => (
                                            <button
                                                key={`${space.id}/${folder.id || folder.name}/${list.id}`}
                                                onClick={() => onListNavigate(space.id, folder.id || null, list.id)}
                                                className="w-full flex items-center gap-2 px-3 py-1.5 pl-8 text-left hover:bg-surface-hover transition-colors"
                                            >
                                                <Hash className="w-3 h-3 text-accent-emerald/70" />
                                                <span className="flex-1 text-[11px] text-text-secondary truncate">{list.name}</span>
                                                <span className="text-[10px] text-text-quaternary tabular-nums">{list.taskCount}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// Mini Stat Component
interface MiniStatProps {
    icon: React.ReactNode;
    value: number | string;
    color: 'emerald' | 'blue' | 'rose' | 'violet';
    label: string;
}

function MiniStat({ icon, value, color, label }: MiniStatProps) {
    const colorClasses = {
        emerald: 'text-accent-emerald',
        blue: 'text-accent-blue',
        rose: 'text-accent-rose',
        violet: 'text-accent-violet',
    };

    return (
        <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
                <span className={colorClasses[color]}>{icon}</span>
                <span className="text-[12px] font-bold text-text-primary tabular-nums">{value}</span>
            </div>
            <span className="text-[9px] text-text-quaternary uppercase tracking-wider">{label}</span>
        </div>
    );
}

// Space List Row Component (for hierarchical list view)
interface SpaceListRowProps {
    space: {
        name: string;
        taskCount: number;
        completedTasks: number;
        inProgressTasks: number;
        blockedTasks: number;
        overdueTasks: number;
        completionRate: number;
        folders: Array<{ id?: string; name: string; lists: Array<{ id: string; name: string; taskCount: number }>; taskCount: number }>;
        listsCount: number;
    };
    isExpanded: boolean;
    onToggle: () => void;
    onNavigate: () => void;
}

// Wrapper that renders SpaceListRow + expanded folder/list children
interface SpaceListRowWithChildrenProps {
    space: Omit<SpaceListRowProps['space'], 'listsCount'> & { id: string };
    listsCount: number;
    isExpanded: boolean;
    tasks: Task[];
    calculateStats: (taskList: Task[]) => {
        completedTasks: number;
        inProgressTasks: number;
        blockedTasks: number;
        overdueTasks: number;
        completionRate: number;
    };
    onToggle: () => void;
    onNavigate: () => void;
    onFolderNavigate: (spaceId: string, folderId: string) => void;
    onListNavigate: (spaceId: string, folderId: string | null, listId: string) => void;
}

function SpaceListRowWithChildren({
    space,
    listsCount,
    isExpanded,
    tasks,
    calculateStats,
    onToggle,
    onNavigate,
    onFolderNavigate,
    onListNavigate,
}: SpaceListRowWithChildrenProps) {
    return (
        <div>
            <SpaceListRow
                space={{ ...space, listsCount }}
                isExpanded={isExpanded}
                onToggle={onToggle}
                onNavigate={onNavigate}
            />
            {isExpanded && (
                <div className="ml-8 mt-2 space-y-2">
                    {space.folders.map((folder) => {
                        const folderTasks = tasks.filter(t =>
                            t.spaceId === space.id &&
                            t.folderPath?.includes(folder.name)
                        );
                        const folderStats = calculateStats(folderTasks);
                        return (
                            <div key={`${space.id}/${folder.id || folder.name}`} className="rounded-lg border border-border-primary/50 bg-surface-primary overflow-hidden">
                                <button
                                    onClick={() => folder.id ? onFolderNavigate(space.id, folder.id) : undefined}
                                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-surface-hover transition-colors"
                                >
                                    <Folder className="w-4 h-4 text-accent-blue" />
                                    <span className="flex-1 text-[12px] font-medium text-text-primary truncate text-left">{folder.name}</span>
                                    <span className="text-[10px] text-text-quaternary tabular-nums">{folder.taskCount} task</span>
                                    <span className="text-[10px] text-accent-emerald tabular-nums">{folderStats.completionRate}%</span>
                                    <ChevronRight className="w-3.5 h-3.5 text-text-quaternary" />
                                </button>
                                {folder.lists.length > 0 && (
                                    <div className="border-t border-border-primary/30">
                                        {folder.lists.map((list) => (
                                            <button
                                                key={`${space.id}/${folder.id || folder.name}/${list.id}`}
                                                onClick={() => onListNavigate(space.id, folder.id || null, list.id)}
                                                className="w-full flex items-center gap-2 px-3 py-1.5 pl-8 text-left hover:bg-surface-hover transition-colors"
                                            >
                                                <Hash className="w-3 h-3 text-accent-emerald/70" />
                                                <span className="flex-1 text-[11px] text-text-secondary truncate">{list.name}</span>
                                                <span className="text-[10px] text-text-quaternary tabular-nums">{list.taskCount}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function SpaceListRow({ space, isExpanded, onToggle, onNavigate }: SpaceListRowProps) {
    return (
        <div className={clsx(
            "rounded-xl border transition-all",
            isExpanded
                ? "bg-surface-secondary border-accent-violet/30 shadow-lg"
                : "bg-surface-primary border-border-primary hover:border-border-secondary hover:shadow-md"
        )}>
            <div className="p-4">
                {/* Header Row */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={onToggle}
                        className="shrink-0"
                    >
                        <ChevronRight className={clsx(
                            "w-5 h-5 text-text-quaternary transition-transform",
                            isExpanded && "rotate-90"
                        )} />
                    </button>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h4
                                onClick={onNavigate}
                                className="text-[13px] font-semibold text-text-primary truncate hover:text-accent-violet cursor-pointer transition-colors"
                            >
                                {space.name}
                            </h4>
                            <span className="px-2 py-0.5 rounded-full text-[9px] font-semibold bg-surface-tertiary text-text-quaternary uppercase shrink-0">
                                Space
                            </span>
                        </div>
                        <p className="text-[10px] text-text-quaternary mt-0.5">
                            {space.folders.length} cartelle · {space.listsCount} liste
                        </p>
                    </div>
                    <span className="px-2.5 py-1 rounded-lg bg-surface-tertiary text-[11px] font-bold text-text-secondary tabular-nums shrink-0">
                        {space.taskCount.toLocaleString()} task
                    </span>
                </div>

                {/* Progress Bar */}
                <div className="mt-3 ml-8">
                    <div className="flex items-center justify-between text-[9px] mb-1">
                        <span className="text-text-quaternary uppercase tracking-wider font-medium">Completamento</span>
                        <span className="text-text-secondary font-semibold tabular-nums">{space.completionRate}%</span>
                    </div>
                    <div className="h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
                        <div
                            className="h-full bg-accent-emerald rounded-full transition-all"
                            style={{ width: `${space.completionRate}%` }}
                        />
                    </div>
                </div>

                {/* Stats Row */}
                <div className="flex items-center justify-between mt-3 ml-8">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                            <Sparkles className="w-3.5 h-3.5 text-accent-emerald" />
                            <span className="text-[11px] font-semibold text-text-primary tabular-nums">{space.completedTasks}</span>
                            <span className="text-[9px] text-text-quaternary hidden sm:inline">completati</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Zap className="w-3.5 h-3.5 text-accent-blue" />
                            <span className="text-[11px] font-semibold text-text-primary tabular-nums">{space.inProgressTasks}</span>
                            <span className="text-[9px] text-text-quaternary hidden sm:inline">in corso</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <ShieldAlert className="w-3.5 h-3.5 text-accent-rose" />
                            <span className="text-[11px] font-semibold text-text-primary tabular-nums">{space.blockedTasks}</span>
                            <span className="text-[9px] text-text-quaternary hidden sm:inline">bloccati</span>
                        </div>
                    </div>

                    {/* Overdue Warning */}
                    {space.overdueTasks > 0 && (
                        <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-accent-rose/10">
                            <Flame className="w-3 h-3 text-accent-rose" />
                            <span className="text-[9px] text-accent-rose font-semibold">{space.overdueTasks} in ritardo</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

